/* eslint-disable max-len */
import {
  all,
  put,
  call,
  delay,
  select,
  takeLatest,
  take,
  cancel,
  fork,
  actionChannel,
  flush,
  takeEvery,
  race
} from "redux-saga/effects";

import { get } from "lodash";
import { getErrorScreensIcons } from "../../utils/getErrorScreenIcons/getErrorScreenIcons";
import {
  SEND_PROSPECT_TO_API,
  sendProspectToAPISuccess,
  sendProspectToAPIFail,
  resetFormStep,
  PROSPECT_AUTO_SAVE,
  sendProspectRequest,
  SEND_PROSPECT_REQUEST,
  setScreeningError,
  PROSPECT_SAVE_ONCLICK,
  sendProspectToAPIAutoSaveSuccess,
  updateAutoSaveProspectHash,
  setIsContactAndSignatoryDetailsChanged,
  SEND_PROSPECT_TO_API_SUCCESS,
  SEND_PROSPECT_TO_API_FAIL
} from "../actions/sendProspectToAPI";
import { log } from "../../utils/loggger";
import {
  getProspect,
  getProspectId,
  getAuthorizationHeader,
  getAccountType,
  getIsIslamicBanking,
  getAuthToken,
  getApplicationInfo,
  getSignatories,
  getIsAutoSaveEnabled,
  getApplicantInfo,
  getIsMultiPartnerJourney
} from "../selectors/appConfig";
import { checkLoginStatus, getLoginResponse } from "../selectors/loginSelector";
import { getCompletedSteps } from "../selectors/completedSteps";
import { getProspectAutoSaveHash, getScreeningError } from "../selectors/sendProspectToAPI";
import { setErrorOccurredWhilePerforming } from "../actions/searchProspect";
import { resetInputsErrors, setInputsErrors } from "../actions/serverValidation";
import { updateAccountNumbers } from "../actions/accountNumbers";
import { otp, prospect } from "../../api/apiClient";
import {
  APP_STOP_SCREEN_RESULT,
  screeningStatus,
  screeningStatusDefault,
  AUTO,
  VIEW_IDS,
  AUTO_SAVE_INTERVAL,
  applicationError,
  operatorLoginScheme,
  EFR_CHECK_ERROR,
  EFR_CHECK,
  INELIGIBLE_COUNTRY_CHECK,
  INELIGIBLE_COUNTRY_CHECK_COUNTRY_OF_BIRTH,
  INELIGIBLE_COUNTRY_CHECK_TAX_DECLARATION,
  applicationDedupeError,
  OtpChannel,
  NEXT,
  SCREENING_FAIL_REASONS
} from "../../constants";
import {
  resetProspect,
  setTat,
  updateProspect,
  setMobileEmailChange,
  SET_CONFIG
} from "../actions/appConfig";
import { FieldsValidationError, ErrorOccurredWhilePerforming } from "../../api/serverErrors";
import { pageProspectPaylodMap } from "../../constants/config";
import { NOTIFY_HOST } from "../actions/kyc";
import { OPE_EDIT } from "../../containers/AgentPages/SearchedAppInfo/constants";
import { getSearchResults } from "../selectors/searchProspect";
import { FeatureFlags } from "../../utils/constants";
import { smsOtpVerified } from "../actions/otp";
import {
  getSelectedSignatoryIndex,
  getSelectedStakeholder,
  getSelectedStakeholderId
} from "../selectors/stakeholders";
import { getwtmSessionDetails } from "../selectors/webToMobile";
import { transformProspectData } from "./transformProspectDataUtil";
import { checkIfEfrNotRequiredForNonSignatory } from "../../utils/checkIfEfrNotRequiredForNonSignatory";

function checkIsAutoSaveViewId(viewId) {
  return [
    VIEW_IDS.CompanyInfo,
    VIEW_IDS.StakeholdersInfo,
    VIEW_IDS.StakeholdersInfoPreview,
    VIEW_IDS.ConsentInfo,
    VIEW_IDS.AdditionalInfo,
    VIEW_IDS.CompanyAdditionalInfo,
    VIEW_IDS.StakeholdersAdditionalInfo,
    VIEW_IDS.AccountInfo,
    VIEW_IDS.SubmitApplication,
    VIEW_IDS.AdditionaData,
    VIEW_IDS.AccountServices,
    VIEW_IDS.FinalQuestions,
    VIEW_IDS.UploadDocuments,
    VIEW_IDS.UploadDocuments,
    VIEW_IDS.SelectServices,
    VIEW_IDS.PartnershipDetailsInfo,
    VIEW_IDS.PartnerDetailsInfo,
    VIEW_IDS.PartnershipSummaryInfo
  ].includes(viewId);
}

const getOtpPayloadData = (email, countryCode, mobileNo, mode, prospectId) => {
  return {
    email,
    countryCode,
    mobileNo,
    action: "generate",
    mode,
    prospectId
  };
};

/**
 * Gets OTP generation payload for partnership summary info view
 * @param {Object} selectedSignatory - Selected signatory data
 * @param {string} prospectId - Prospect ID
 * @returns {Object} OTP generation payload
 */
function getPartnershipSummaryOtpPayload(selectedSignatory, prospectId) {
  const {
    signatoryId,
    contactDetails: { primaryEmail, primaryMobCountryCode, primaryMobileNo }
  } = selectedSignatory;

  return {
    email: primaryEmail,
    countryCode: primaryMobCountryCode,
    mobileNo: primaryMobileNo,
    action: "generate",
    mode: OtpChannel.Sms,
    prospectId,
    sid: signatoryId
  };
}

/**
 * Gets OTP generation payload for stakeholder info preview (signatory changes)
 * @param {Object} params - Parameters needed for payload generation
 * @returns {Object} OTP generation payload
 */
function getStakeholderSignatoryOtpPayload({
  isSignatoryEmailIdChanged,
  isSignatoryMobileNumberChanged,
  selectedSignatory,
  prospectId
}) {
  const {
    signatoryId,
    contactDetails: {
      editedPrimaryEmailId,
      editedPrimaryMobileNo,
      primaryEmail,
      primaryMobCountryCode,
      primaryMobileNo
    }
  } = selectedSignatory;

  return {
    email: isSignatoryEmailIdChanged ? editedPrimaryEmailId : primaryEmail,
    countryCode: primaryMobCountryCode,
    mobileNo: isSignatoryMobileNumberChanged ? editedPrimaryMobileNo : primaryMobileNo,
    action: "generate",
    mode: isSignatoryMobileNumberChanged ? OtpChannel.Sms : OtpChannel.Email,
    prospectId,
    sid: signatoryId
  };
}

/**
 * Gets OTP generation payload for stakeholder info preview (company changes)
 * @param {Object} params - Parameters needed for payload generation
 * @returns {Object} OTP generation payload
 */
function getStakeholderCompanyOtpPayload({
  isEmailIdChanged,
  isMobileNumberChanged,
  selectedSignatory,
  applicantInfo,
  prospectId
}) {
  const {
    signatoryId,
    editedMobileNumber,
    editedEmailId,
    contactDetails: { primaryMobCountryCode }
  } = selectedSignatory;

  return {
    email: isEmailIdChanged ? editedEmailId : applicantInfo?.email,
    countryCode: primaryMobCountryCode,
    mobileNo: isMobileNumberChanged ? editedMobileNumber : applicantInfo?.mobileNo,
    action: "generate",
    mode: isMobileNumberChanged ? OtpChannel.Sms : OtpChannel.Email,
    prospectId,
    sid: signatoryId,
    isRequestedByCompany: true
  };
}

/**
 * Gets OTP generation payload for non-multi partner journey
 * @param {Object} params - Parameters needed for payload generation
 * @returns {Object} OTP generation payload
 */
function getNonMultiPartnerOtpPayload({
  isEmailIdChanged,
  isMobileNumberChanged,
  signatoryInfo,
  applicantInfo,
  prospectId
}) {
  const emailID = isEmailIdChanged ? signatoryInfo[0]?.editedEmailId : applicantInfo.email;
  const countryCode = applicantInfo.countryCode;
  const mobileNumber = isMobileNumberChanged
    ? signatoryInfo[0]?.editedMobileNumber
    : applicantInfo.mobileNo;
  const mode = isMobileNumberChanged ? OtpChannel.Sms : OtpChannel.Email;

  return getOtpPayloadData(emailID, countryCode, mobileNumber, mode, prospectId);
}

/**
 * Determines the appropriate OTP payload based on view and journey type
 * @param {Object} params - Parameters needed for payload determination
 * @returns {Object} OTP generation payload
 */
function getOtpPayload({
  viewId,
  isMultiPartnerJourney,
  selectedSignatory,
  prospectId,
  isSignatoryEmailIdChanged,
  isSignatoryMobileNumberChanged,
  isMobileNumberChanged,
  isEmailIdChanged,
  applicantInfo,
  signatoryInfo
}) {
  if (isMultiPartnerJourney) {
    if (viewId === VIEW_IDS.PartnershipSummaryInfo) {
      return getPartnershipSummaryOtpPayload(selectedSignatory, prospectId);
    }

    if (viewId === VIEW_IDS.StakeholdersInfoPreview) {
      if (isSignatoryEmailIdChanged || isSignatoryMobileNumberChanged) {
        return getStakeholderSignatoryOtpPayload({
          isSignatoryEmailIdChanged,
          isSignatoryMobileNumberChanged,
          selectedSignatory,
          prospectId
        });
      }

      if (isMobileNumberChanged || isEmailIdChanged) {
        return getStakeholderCompanyOtpPayload({
          isEmailIdChanged,
          isMobileNumberChanged,
          selectedSignatory,
          applicantInfo,
          prospectId
        });
      }
    }

    return null;
  }

  return getNonMultiPartnerOtpPayload({
    isEmailIdChanged,
    isMobileNumberChanged,
    signatoryInfo,
    applicantInfo,
    prospectId
  });
}

/**
 * Handles OTP verification process
 * @param {Object} data - Data from API response
 * @param {boolean} isSignatoryEmailIdChanged - Whether signatory email was changed
 * @param {boolean} isSignatoryMobileNumberChanged - Whether signatory mobile was changed
 * @param {boolean} isScreeningError - Whether there is a screening error
 * @param {string} viewId - Current view ID
 * @param {boolean} isContactAndSignatoryDetailsChanged - Whether both contact and signatory details changed
 * @param {boolean} isAgent - Whether the user is an agent
 */
function* handleOTPVerification(
  data,
  isSignatoryEmailIdChanged,
  isSignatoryMobileNumberChanged,
  isScreeningError,
  viewId,
  isContactAndSignatoryDetailsChanged,
  isAgent
) {
  const { isMobileNumberChanged, isEmailIdChanged } = data;
  const applicantInfo = yield select(getApplicantInfo);
  const headers = yield select(getAuthorizationHeader);
  const prospectId = yield select(getProspectId);
  const signatoryInfo = yield select(getSignatories);
  const isMultiPartnerJourney = yield select(getIsMultiPartnerJourney);
  const selectedSignatory = yield select(getSelectedStakeholder);

  // Update Redux state
  yield put(smsOtpVerified());
  yield put(
    setMobileEmailChange({
      isMobileNumberChanged,
      isEmailIdChanged,
      isSignatoryEmailIdChanged,
      isSignatoryMobileNumberChanged
    })
  );

  // Generate OTP if needed
  if (!isContactAndSignatoryDetailsChanged) {
    const otpGenerationPayload = yield getOtpPayload({
      viewId,
      isMultiPartnerJourney,
      selectedSignatory,
      prospectId,
      isSignatoryEmailIdChanged,
      isSignatoryMobileNumberChanged,
      isMobileNumberChanged,
      isEmailIdChanged,
      applicantInfo,
      signatoryInfo
    });

    if (otpGenerationPayload && !isAgent) {
      //for Agent OTP page is not required, so directly sending the data to API
      yield call(otp.generate, { ...otpGenerationPayload }, headers);
    }
  }

  // Return success response
  yield put(
    sendProspectToAPISuccess({
      isScreeningError: !!isScreeningError,
      isMobileChanged: isMobileNumberChanged,
      isEmailChanged: isEmailIdChanged,
      isSignatoryEmailIdChanged,
      isSignatoryMobileNumberChanged,
      isContactAndSignatoryDetailsChanged
    })
  );
}

/**
 * Determines which action to process from a list of pending actions
 * Prioritizes user actions over auto-save actions
 *
 * @param {Array} actions - List of pending actions
 * @returns {Object} The action to process
 */
function selectActionToProcess(actions) {
  if (!actions.length) return null;

  const userActions = actions.filter(act => act.payload.saveType !== AUTO);
  return userActions.length > 0 ? userActions[0] : actions[0];
}

/**
 * Processes a single prospect save action
 *
 * @param {Object} action - The action to process
 * @param {boolean} isAutosaveViewId - Whether the current view supports auto-save
 * @yields {Object} Redux saga effects
 */
function* processProspectSaveAction(action, isAutosaveViewId) {
  if (!action || !isAutosaveViewId) return;

  if (action.payload.saveType === NEXT) {
    // Ensure state updates have completed before proceeding
    yield race({
      updateAction: take(SET_CONFIG),
      timeout: delay(500)
    });
  }

  // Process the selected action with error boundaries
  yield call(sendProspectToAPI, action);
}

/**
 * Watches for prospect save requests and processes them with improved batching
 * Ensures user actions are prioritized over auto-save actions
 *
 * @yields {Object} Redux saga effects
 */
export function* watchRequest() {
  const chan = yield actionChannel(SEND_PROSPECT_REQUEST);

  while (true) {
    try {
      // Collect all pending actions
      const actions = yield flush(chan);

      if (actions.length) {
        const actionToProcess = selectActionToProcess(actions);
        const newProspect = yield select(getProspect);
        const viewId = newProspect.applicationInfo.viewId;
        const isAutosaveViewId = checkIsAutoSaveViewId(viewId);

        yield call(processProspectSaveAction, actionToProcess, isAutosaveViewId);
      }

      // Dynamic delay based on pending actions
      // Shorter delay when actions are pending, longer when idle
      yield delay(actions.length > 0 ? 300 : 1000);
    } catch (error) {
      log("Error in watchRequest saga:", error);
    }
  }
}

export function* setScreeningResults({ preScreening }) {
  const currScreeningType = preScreening.screeningResults.find(
    screeningResult =>
      screeningResult.screeningNotes &&
      SCREENING_FAIL_REASONS.includes(screeningResult.screeningReason)
  );

  let screenError = screeningStatus.find(
    ({ screeningType }) => screeningType === (currScreeningType || {}).screeningType
  );

  if (screenError) {
    //ro-assist-brd1-3
    // check if Ineligible country check and application dedupe is included in screeninStatus
    if (
      [screeningStatus[1]?.screeningType.toLowerCase()].includes(
        screenError.screeningType?.toLowerCase()
      )
    ) {
      let buttons = applicationDedupeError.find(
        ({ screeningNotes }) =>
          screeningNotes.toLowerCase() === currScreeningType.screeningNotes?.toLowerCase()
      );
      if (buttons !== undefined) {
        screenError = { ...screenError, ...buttons };
      }
    }
    if (
      [
        INELIGIBLE_COUNTRY_CHECK.toLowerCase(),
        INELIGIBLE_COUNTRY_CHECK_TAX_DECLARATION.toLowerCase(),
        INELIGIBLE_COUNTRY_CHECK_COUNTRY_OF_BIRTH.toLowerCase()
      ].includes(screenError.screeningType?.toLowerCase())
    ) {
      let buttons = applicationError.find(
        ({ screeningNotes }) =>
          screeningNotes.toLowerCase() === currScreeningType.screeningNotes?.toLowerCase()
      );
      if (buttons !== undefined) {
        screenError = { ...screenError, ...buttons };
      }
    }
    const accountType = yield select(getAccountType);
    const isIslamicBanking = yield select(getIsIslamicBanking);
    const { screeningType, screeningNotes } = screenError;
    if (screenError.screeningType?.toLowerCase() === EFR_CHECK.toLowerCase()) {
      yield put(
        setScreeningError({
          ...screenError,
          text: EFR_CHECK_ERROR,
          icon: "",
          screeningType: 403,
          isEFRDeclinedScreen: true
        })
      );
    } else {
      yield put(
        setScreeningError({
          ...screenError,
          text: currScreeningType.reasonNotes,
          icon: getErrorScreensIcons(accountType, isIslamicBanking, screeningType, screeningNotes)
        })
      );
    }
  } else {
    yield put(
      setScreeningError({
        ...screeningStatusDefault,
        text: currScreeningType?.reasonNotes || screeningStatusDefault.text
      })
    );
  }
}

export function* sendProspectToAPISaga({ payload: { saveType, actionType, step } }) {
  try {
    yield put(resetInputsErrors());
    yield put(resetFormStep(true));

    const prospect = yield select(getProspect);

    yield put(sendProspectRequest(prospect, saveType, actionType, step));
  } finally {
    yield put(resetFormStep(false));
  }
}

function* saveProspectData() {
  try {
    const newProspect = yield select(getProspect);
    const screeningError = yield select(getScreeningError);
    const isScreeningError = screeningError.error;
    const viewId = newProspect.applicationInfo.viewId;
    const authToken = yield select(getAuthToken);
    const isSaveEnabled = !isScreeningError && authToken && checkIsAutoSaveViewId(viewId);
    return { isSaveEnabled, newProspect };
  } catch (e) {
    log(e);
    return { isSaveEnabled: false, newProspect: null };
  }
}
export function* prospectSaveOnClick() {
  try {
    const { isSaveEnabled, newProspect } = yield call(saveProspectData);
    if (isSaveEnabled && newProspect) {
      yield put(sendProspectRequest(newProspect, AUTO));
    }

    yield put(resetProspect);

    return { isSaveEnabled, newProspect };
  } catch (e) {
    log(e);
  }
}

export async function generateHash(value) {
  // Convert the JSON string to a Uint8Array
  const encoder = new TextEncoder();
  const data = encoder.encode(JSON.stringify(value));

  // Use the SubtleCrypto API to create a hash (SHA-256 in this example)
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  // Convert the hash to a hexadecimal string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(byte => byte.toString(16).padStart(2, "0")).join("");
}

function preventAutoSave(viewId) {
  return [VIEW_IDS.SubmitApplication].includes(viewId);
}

export function* sendSignatoryIdForV3UpdateProspect(viewId) {
  const multiPartnerId = yield select(getSelectedStakeholderId);

  if (
    multiPartnerId &&
    [
      VIEW_IDS.StakeholdersInfo,
      VIEW_IDS.StakeholdersInfoPreview,
      VIEW_IDS.ConsentInfo,
      VIEW_IDS.StakeholdersAdditionalInfo
    ].includes(viewId)
  ) {
    return multiPartnerId;
  }
  return false;
}

export function* sendSignatoryIdForV2UpdateProspect(viewId) {
  const multiPartnerId = yield select(getSelectedStakeholderId);
  if (
    multiPartnerId &&
    [
      VIEW_IDS.AccountInfo,
      VIEW_IDS.SubmitApplication,
      VIEW_IDS.AdditionalInfo,
      VIEW_IDS.CompanyAdditionalInfo
    ].includes(viewId)
  ) {
    return multiPartnerId;
  }
  return false;
}

export function* prospectAutoSave() {
  try {
    const autoSave = yield select(getIsAutoSaveEnabled);
    const isAutoSaveEnabled = autoSave?.[0]?.displayText === "true";
    const newProspect = yield select(getProspect);
    const viewId = newProspect.applicationInfo.viewId;
    let autoSaveEnabled = isAutoSaveEnabled;

    while (autoSaveEnabled) {
      yield delay(AUTO_SAVE_INTERVAL);

      const { isSaveEnabled, newProspect } = yield call(saveProspectData);
      autoSaveEnabled = isSaveEnabled;

      if (autoSaveEnabled && !preventAutoSave(viewId)) {
        const currentProspectHash = yield call(generateHash, newProspect);
        const lastSavedProspectHash = yield select(getProspectAutoSaveHash);
        yield put(updateAutoSaveProspectHash(currentProspectHash));
        if (lastSavedProspectHash !== currentProspectHash) {
          yield put(sendProspectRequest(newProspect, AUTO));
        }
      }
    }
  } catch (e) {
    log(e);
  }
}

const getRequestPayloadForNode = ({
  key,
  prospect,
  viewId,
  isAgent,
  isEditable,
  isIslamicBanking,
  isMultiPartnerJourney,
  currentIndex
}) => {
  let nodePayload;
  const isCrossSellFeatureEnabled = FeatureFlags["CROSS_SELL_FEATURE_ENABLED"] ?? false;
  const isKycRemedionForCompanyInfoEnabled =
    process.env.REACT_APP_KYC_REMEDIATION_COMPANY_INFO_FIELDS === "Y";
  const isTaxInfoKycRemediationEnabled = process.env.REACT_APP_KYC_REMEDIATION_TAX_FIELDS === "Y";
  const isHawalaKycRemediationEnabled = process.env.REACT_APP_KYC_REMEDIATION_HAWALA_FIELDS === "Y";
  const isOtherBankingKycRemediationEnabled =
    process.env.REACT_APP_KYC_REMEDIATION_BANK_DETAILS === "Y";
  const isBranchesAndSubsidiariesEnabled =
    process.env.REACT_APP_KYC_REMEDIATION_BRANCHES_SUBSIDIARIES === "Y";

  nodePayload = transformProspectData({
    prospect,
    key,
    viewId,
    currentIndex,
    isMultiPartnerJourney,
    isEditable,
    isKycRemedionForCompanyInfoEnabled,
    isIslamicBanking,
    isCrossSellFeatureEnabled,
    isTaxInfoKycRemediationEnabled,
    isHawalaKycRemediationEnabled,
    isOtherBankingKycRemediationEnabled,
    isBranchesAndSubsidiariesEnabled,
    isAgent
  });

  return nodePayload;
};

function* handleOTPManagement(data, isScreeningError, viewId, isAgent) {
  const { isMobileNumberChanged, isEmailIdChanged } = data;
  const isSignatoryEmailIdChanged = !!data?.signatoryInfo?.[0]?.isSignatoryEmailIdChanged;
  const isSignatoryMobileNumberChanged = !!data?.signatoryInfo?.[0]?.isSignatoryMobileNumberChanged;
  const isDataChanged =
    isMobileNumberChanged ||
    isEmailIdChanged ||
    isSignatoryEmailIdChanged ||
    isSignatoryMobileNumberChanged;
  const isContactAndSignatoryDetailsChanged =
    (isMobileNumberChanged || isEmailIdChanged) &&
    (isSignatoryEmailIdChanged || isSignatoryMobileNumberChanged);

  const selectedStakeholder = yield select(getSelectedStakeholder);

  const checkIfEfrNotRequired = checkIfEfrNotRequiredForNonSignatory(selectedStakeholder);

  if (isContactAndSignatoryDetailsChanged) {
    yield put(setIsContactAndSignatoryDetailsChanged(true));
    yield call(
      handleOTPVerification,
      data,
      isSignatoryEmailIdChanged,
      isSignatoryMobileNumberChanged,
      isScreeningError,
      viewId,
      isContactAndSignatoryDetailsChanged,
      isAgent
    );
  } else if (
    (viewId === "/StakeholdersInfoPreview" && isDataChanged) ||
    (viewId === "/PartnershipSummaryInfo" &&
      (!checkIfEfrNotRequired || selectedStakeholder?.kycDetails.isSignatory))
  ) {
    yield call(
      handleOTPVerification,
      data,
      isSignatoryEmailIdChanged,
      isSignatoryMobileNumberChanged,
      isScreeningError,
      viewId,
      isAgent
    );
  } else if (viewId === "/PartnershipDetailsInfo") {
    yield put(
      sendProspectToAPISuccess({
        isScreeningError: !!isScreeningError,
        data
      })
    );
  } else {
    yield put(sendProspectToAPISuccess(!!isScreeningError));
  }
}

/**
 * Gets common data required for processing the prospect
 * @returns {Object} The common data needed for processing
 */
function* getProspectProcessingData() {
  return {
    scheme: (yield select(getLoginResponse)).scheme,
    prospectId: yield select(getProspectId),
    headers: yield select(getAuthorizationHeader),
    completedSteps: yield select(getCompletedSteps),
    applicationInfo: yield select(getApplicationInfo),
    signatoryInfo: yield select(getSignatories),
    isAgent: yield select(checkLoginStatus),
    searchResults: yield select(getSearchResults),
    isIslamicBanking: yield select(getIsIslamicBanking),
    isMultiPartnerJourney: yield select(getIsMultiPartnerJourney),
    selectedSignatoryId: yield select(getSelectedStakeholderId),
    currentIndex: yield select(getSelectedSignatoryIndex)
  };
}

/**
 * Prepares the prospect payload for API submission
 */
function* prepareProspectPayload(newProspect, saveType, actionType, commonData) {
  const {
    applicationInfo,
    isAgent,
    isMultiPartnerJourney,
    isIslamicBanking,
    currentIndex,
    searchResults,
    prospectId,
    scheme
  } = commonData;

  const viewId = applicationInfo.viewId;
  const currentProspect = searchResults.find(item => item.prospectId === prospectId);
  const isFrontCorrection = get(currentProspect, "status.statusType") === OPE_EDIT;
  const isOperator = scheme === operatorLoginScheme;
  const isEditable = isOperator && isFrontCorrection;

  // Update prospect with new data
  newProspect.applicationInfo.saveType = saveType;
  newProspect.applicationInfo.actionType = actionType;

  // Build payload
  const payloadKeys = pageProspectPaylodMap[viewId];
  const createProspectPayload = {};

  payloadKeys &&
    payloadKeys.forEach(key => {
      if (newProspect[key]) {
        createProspectPayload[key] = getRequestPayloadForNode({
          key,
          prospect: newProspect,
          viewId,
          isAgent,
          isEditable,
          isIslamicBanking,
          isMultiPartnerJourney,
          currentIndex
        });
      }
    });

  // Add signatory IDs if needed
  const multiPartnerId = yield sendSignatoryIdForV3UpdateProspect(viewId);
  const isV2UpdateProspect = yield sendSignatoryIdForV2UpdateProspect(viewId);

  if (multiPartnerId) {
    createProspectPayload.sid = multiPartnerId;
  }

  if (isV2UpdateProspect) {
    createProspectPayload.sid = isV2UpdateProspect;
  }

  // Add common fields
  createProspectPayload.viewId = viewId;
  createProspectPayload.actionType = actionType;
  createProspectPayload.saveType = saveType;

  return { createProspectPayload, viewId, multiPartnerId };
}

/**
 * Process the API response data
 */
function* processApiResponse(data, newProspect, saveType, viewId, commonData) {
  const { isMultiPartnerJourney, signatoryInfo, selectedSignatoryId } = commonData;

  // Handle account info update
  if (data.accountInfo && Array.isArray(data.accountInfo)) {
    yield call(processAccountInfo, data, newProspect);
  }

  // Handle signatory info update
  if (data.signatoryInfo && Array.isArray(data.signatoryInfo) && saveType !== "auto") {
    yield call(
      processSignatoryInfo,
      data,
      signatoryInfo,
      isMultiPartnerJourney,
      selectedSignatoryId
    );
  }

  // Set TAT if applicable
  if (viewId === "/SubmitApplication") {
    yield put(setTat(data.TAT));
  }

  // Update journey type for specific views
  if (viewId === VIEW_IDS.StakeholdersInfo || viewId === VIEW_IDS.ConsentInfo) {
    yield put(updateProspect({ "prospect.userJourneyType": "emailRedirection" }));
  }

  return data;
}

/**
 * Process account information from API response
 */
function* processAccountInfo(data, newProspect) {
  yield put(updateAccountNumbers(data.accountInfo));
  data.accountInfo.forEach(
    (_, index) =>
      (newProspect.accountInfo[index] = {
        ...newProspect.accountInfo[index],
        accountNo: data.accountInfo[index].accountNo
      })
  );
}

/**
 * Process signatory information from API response
 */
function* processSignatoryInfo(data, signatoryInfo, isMultiPartnerJourney, selectedSignatoryId) {
  let signatoryData = [];
  if (isMultiPartnerJourney) {
    const {
      isSignatoryEmailIdChanged,
      isSignatoryMobileNumberChanged,
      signatoryId,
      showSOF,
      ...rest
    } = data.signatoryInfo[0];

    signatoryData = signatoryInfo.map(item => {
      if (item.signatoryId === selectedSignatoryId) {
        return {
          ...item,
          ...rest,
          ...(showSOF !== null && { showSOF })
        };
      }
      return item;
    });
  } else {
    const { signatoryId, showSOF, ...rest } = data.signatoryInfo[0];
    signatoryData = [
      {
        ...signatoryInfo[0],
        ...rest,
        ...(showSOF !== null && { showSOF })
      }
    ];
  }

  yield put(
    updateProspect({
      "prospect.signatoryInfo": signatoryData
    })
  );
}

/**
 * Process screening results from the API response
 */
function* processScreeningResults(data) {
  const { preScreening } = data;
  const isScreeningError = preScreening && preScreening.statusOverAll === APP_STOP_SCREEN_RESULT;

  if (isScreeningError) {
    yield fork(setScreeningResults, data);
  } else if (preScreening) {
    yield put(updateProspect({ "prospect.organizationInfo.screeningInfo": preScreening }));
  }

  return isScreeningError;
}

/**
 * Main function to send prospect data to API
 */
export function* sendProspectToAPI({ payload: { newProspect, saveType, actionType } }) {
  try {
    const {
      sessionData: { webMobileRefId }
    } = yield select(getwtmSessionDetails);
    const isWtm = !!webMobileRefId;
    const commonData = yield call(getProspectProcessingData);
    const { createProspectPayload, viewId, multiPartnerId } = yield call(
      prepareProspectPayload,
      newProspect,
      saveType,
      actionType,
      commonData
    );

    const { data } = multiPartnerId
      ? yield call(
          prospect.updateSignatory,
          commonData.prospectId,
          createProspectPayload,
          commonData.headers,
          multiPartnerId,
          isWtm
        )
      : yield call(
          prospect.update,
          commonData.prospectId,
          createProspectPayload,
          commonData.headers,
          isWtm
        );

    // Process the response
    yield call(processApiResponse, data, newProspect, saveType, viewId, commonData);

    // Handle screening results
    const isScreeningError = yield call(processScreeningResults, data);

    // Handle success actions
    if (saveType !== "auto") {
      yield call(handleOTPManagement, data, isScreeningError, viewId, commonData.isAgent);
    } else {
      yield put(sendProspectToAPIAutoSaveSuccess(!!isScreeningError));
    }
  } catch (error) {
    if (error instanceof ErrorOccurredWhilePerforming) {
      yield put(
        setErrorOccurredWhilePerforming({
          errorCode: error.getErrorCode()
        })
      );
    } else if (error instanceof FieldsValidationError) {
      yield put(setInputsErrors(error.getInputsErrors()));
    } else {
      log(error);
    }
    yield put(sendProspectToAPIFail());
  }
}

/**
 * Handles auto-save flow with improved prioritization of user actions
 * Implements queue-based approach to prevent lost updates
 *
 * @yields {Object} Redux saga effects
 */
export function* prospectAutoSaveFlowSaga() {
  // Create a queue to track pending user actions
  const pendingUserActions = [];

  while (true) {
    try {
      // Start background auto-save process
      const bgSyncAutoSave = yield fork(prospectAutoSave);

      // Wait for action that should interrupt auto-save
      const interruptAction = yield take([SEND_PROSPECT_REQUEST, NOTIFY_HOST]);

      // Check if this is a user-initiated action
      const isUserAction = interruptAction.payload?.saveType !== AUTO;

      // Cancel the background auto-save process
      yield cancel(bgSyncAutoSave);

      // If this is a user action, track it to ensure it completes
      if (isUserAction) {
        pendingUserActions.push(interruptAction);

        // Wait for user action to complete before restarting auto-save
        yield take(action => {
          return (
            (action.type === SEND_PROSPECT_TO_API_SUCCESS ||
              action.type === SEND_PROSPECT_TO_API_FAIL) &&
            pendingUserActions.length > 0
          );
        });

        // Remove the completed action from pending queue
        pendingUserActions.shift();

        // Add small delay to ensure UI updates complete
        yield delay(300);
      }
    } catch (error) {
      // Centralized error logging
      log("Error in auto-save flow:", error);
    }
  }
}

export default function* sendProspectToAPISagas() {
  yield all([
    takeEvery(SEND_PROSPECT_TO_API, sendProspectToAPISaga),
    takeLatest(PROSPECT_AUTO_SAVE, prospectAutoSaveFlowSaga),
    takeLatest(PROSPECT_SAVE_ONCLICK, prospectSaveOnClick),
    fork(watchRequest)
  ]);
}
